import React, { useState } from 'react';
import { X, DollarSign, Calendar, Bell } from 'lucide-react';
import { useFamilyContext } from '../../../contexts/FamilyContext';

export default function AllowanceSettingsModal({ isOpen, onClose }) {
  const { family, kidAllowances, updateKidAllowanceSettings } = useFamilyContext();
  const [isLoading, setIsLoading] = useState(false);
  
  // Initialize form data with current family settings
  const [formData, setFormData] = useState(() => {
    // Get current total weekly allowance
    const totalWeekly = family?.kids?.reduce((total, kid) => {
      const allowance = kidAllowances[kid.id];
      return total + (allowance?.weekly_amount || 0);
    }, 0) || 0;

    return {
      weeklyAllowance: totalWeekly,
      distributionDay: 'Sunday',
      jobNotifications: true,
      autoDistribution: true
    };
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Calculate individual allowances (split evenly among kids for now)
      const kidsCount = family?.kids?.length || 1;
      const individualAllowance = formData.weeklyAllowance / kidsCount;

      // Update each kid's allowance settings
      for (const kid of family?.kids || []) {
        await updateKidAllowanceSettings(kid.id, {
          weekly_amount: individualAllowance,
          distribution_day: formData.distributionDay.toLowerCase(),
          job_notifications_enabled: formData.jobNotifications,
          auto_distribution_enabled: formData.autoDistribution
        });
      }

      onClose();
    } catch (error) {
      console.error('Error updating allowance settings:', error);
      alert('Error updating settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-stone-200 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-teal-600" />
              Allowance Settings
            </h2>
            <button
              onClick={onClose}
              className="text-stone-400 hover:text-stone-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Weekly Allowance */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              Total Weekly Allowance
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-stone-500">$</span>
              </div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.weeklyAllowance}
                onChange={(e) => handleInputChange('weeklyAllowance', parseFloat(e.target.value) || 0)}
                className="w-full pl-8 pr-3 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="0.00"
              />
            </div>
            <p className="text-xs text-stone-500 mt-1">
              {family?.kids?.length > 0 && (
                <>Split evenly: ${(formData.weeklyAllowance / family.kids.length).toFixed(2)} per kid</>
              )}
            </p>
          </div>

          {/* Distribution Day */}
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Distribution Day
            </label>
            <select
              value={formData.distributionDay}
              onChange={(e) => handleInputChange('distributionDay', e.target.value)}
              className="w-full px-3 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="Sunday">Sunday</option>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Saturday">Saturday</option>
            </select>
            <p className="text-xs text-stone-500 mt-1">
              When weekly allowance is automatically distributed
            </p>
          </div>

          {/* Auto Distribution */}
          <div>
            <label className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">Automatic Distribution</div>
                <div className="text-xs text-stone-500">Automatically give allowance each week</div>
              </div>
              <button
                type="button"
                onClick={() => handleInputChange('autoDistribution', !formData.autoDistribution)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.autoDistribution ? 'bg-teal-600' : 'bg-stone-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.autoDistribution ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>
          </div>

          {/* Job Notifications */}
          <div>
            <label className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Job Notifications
                </div>
                <div className="text-xs text-stone-500">Remind kids about available jobs</div>
              </div>
              <button
                type="button"
                onClick={() => handleInputChange('jobNotifications', !formData.jobNotifications)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.jobNotifications ? 'bg-teal-600' : 'bg-stone-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.jobNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>
          </div>

          {/* Kids List */}
          {family?.kids?.length > 0 && (
            <div className="border-t border-stone-200 pt-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Kids</h3>
              <div className="space-y-2">
                {family.kids.map((kid) => {
                  const individualAmount = formData.weeklyAllowance / family.kids.length;
                  return (
                    <div key={kid.id} className="flex items-center justify-between py-2 px-3 bg-stone-50 rounded-lg">
                      <span className="text-sm text-slate-900">{kid.name}</span>
                      <span className="text-sm font-semibold text-slate-900">
                        ${individualAmount.toFixed(2)}/week
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-stone-200 p-6">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 text-stone-600 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 